/**
 * Minimalistický Markdown pre odpovede asistenta: presne to, čo povoľuje systémový
 * prompt (**bold**, *kurzíva*, `kód`, odrážky, číslované zoznamy, odkazy). Výstup sú
 * tokeny → React elementy, žiadne innerHTML (XSS-safe bez sanitizéra). Port mobilného
 * MarkdownText (apps/mobile/components/assistant/MarkdownText.tsx).
 */
export type InlineToken =
  | { type: 'text' | 'bold' | 'italic' | 'code'; text: string }
  | { type: 'link'; text: string; href: string }

export type Block =
  | { type: 'p' | 'heading'; text: string }
  | { type: 'ul' | 'ol'; items: string[] }

const INLINE_RE = /(\[[^\]\n]+?\]\(https?:\/\/[^\s)]+\)|\*\*[^*]+?\*\*|\*[^*\s][^*\n]*?\*|`[^`\n]+?`|https?:\/\/[^\s)]+)/g
const MD_LINK_RE = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/
// Koncová interpunkcia holej URL (bodka na konci vety, zátvorka, čiarka…) patrí textu okolo,
// nie odkazu — rovnako ako mobilný MarkdownText (URL_TRAIL_RE).
const URL_TRAIL_RE = /[.,;:!?)\]}]+$/

export function parseInline(text: string): InlineToken[] {
  const out: InlineToken[] = []
  let last = 0
  for (const m of text.matchAll(INLINE_RE)) {
    const raw = m[0]
    const index = m.index ?? 0
    if (index > last) out.push({ type: 'text', text: text.slice(last, index) })
    const link = MD_LINK_RE.exec(raw)
    if (link) {
      out.push({ type: 'link', text: link[1], href: link[2] })
    } else if (raw.startsWith('**')) {
      out.push({ type: 'bold', text: raw.slice(2, -2) })
    } else if (raw.startsWith('`')) {
      out.push({ type: 'code', text: raw.slice(1, -1) })
    } else if (raw.startsWith('*')) {
      out.push({ type: 'italic', text: raw.slice(1, -1) })
    } else {
      const trail = URL_TRAIL_RE.exec(raw)?.[0] ?? ''
      const url = trail ? raw.slice(0, raw.length - trail.length) : raw
      out.push({ type: 'link', text: url, href: url })
      if (trail) out.push({ type: 'text', text: trail })
    }
    last = index + raw.length
  }
  if (last < text.length) out.push({ type: 'text', text: text.slice(last) })
  return out
}

const UL_RE = /^\s*[-*•]\s+(.*)$/
const OL_RE = /^\s*\d+[.)]\s+(.*)$/
const HEADING_RE = /^\s*#{1,6}\s+(.*)$/

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = []
  for (const line of text.split('\n')) {
    if (!line.trim()) continue
    const ul = UL_RE.exec(line)
    const ol = ul ? null : OL_RE.exec(line)
    const prev = blocks[blocks.length - 1]
    if (ul || ol) {
      const type = ul ? 'ul' : 'ol'
      const item = (ul ?? ol)![1]
      if (prev && prev.type === type) prev.items.push(item)
      else blocks.push({ type, items: [item] })
      continue
    }
    const heading = HEADING_RE.exec(line)
    blocks.push(heading ? { type: 'heading', text: heading[1] } : { type: 'p', text: line })
  }
  return blocks
}
